import React, { useState, useEffect, useMemo, useRef } from 'react'
import ReactDOM from 'react-dom'
import lottie from 'lottie-web'

import { useWebsocket } from '~components/WebsocketEvents'
import { useThemeUpdate } from 'shared-ui/assets/styles/PortalThemeProvider'
import IntlUtil from 'shared-ui/utils/IntlUtil'
import { getPointsLabel } from 'shared-ui/utils/points'
import { useMember } from 'shared-ui/components/pages/PageWrapper'

const earnedSound = '/sounds/earned.mp3'
const burnedSound = '/sounds/burned.mp3'
const DEBOUNCE_DELAY = 500

// bidi helpers
const NBSP = '\u00A0'
const RLM = '\u200F'
const RLE = '\u202B'
const PDF = '\u202C'

const PointsAnimation = ({ enableSound }) => {
  const { jpointsChanges, setJpointsChanges } = useWebsocket()
  const [currentAnimation, setCurrentAnimation] = useState(null)
  const [isWaitingForChanges, setIsWaitingForChanges] = useState(false)
  const [stableJpointsChanges, setStableJpointsChanges] = useState([])
  const { colorScheme } = useThemeUpdate()
  const [lottieJson, setLottieJson] = useState(null)
  const [lastColorScheme, setLastColorScheme] = useState(colorScheme)
  const member = useMember()

  const audioRef = useRef(null)
  const soundTimeoutRef = useRef(null)
  const debounceTimerRef = useRef(null)
  const previousLengthRef = useRef(0)

  const containerRef = useRef(null)
  const animRef = useRef(null)

  const isRTL = () => {
    if (typeof document === 'undefined') return false
    return document.body?.getAttribute('dir') === 'rtl'
  }

  // "123 label" with proper bidi
  const formatPointsText = (difference, label) => {
    const n = IntlUtil.num(Math.abs(difference))
    const lineLTR = `${n} ${label}`
    const lineRTL = `${n}${RLM}${NBSP}${label}`
    return isRTL() ? `${RLE}${lineRTL}${PDF}` : lineLTR
  }

  // Debounce burst changes
  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    if (jpointsChanges.length > 0 && jpointsChanges.length !== previousLengthRef.current) {
      previousLengthRef.current = jpointsChanges.length
      setIsWaitingForChanges(true)
      debounceTimerRef.current = setTimeout(() => {
        setStableJpointsChanges([...jpointsChanges])
        setIsWaitingForChanges(false)
      }, DEBOUNCE_DELAY)
    }
    return () => debounceTimerRef.current && clearTimeout(debounceTimerRef.current)
  }, [jpointsChanges])

  // Pick current animation from the stable queue
  useEffect(() => {
    if (stableJpointsChanges.length > 0 && !currentAnimation && !isWaitingForChanges) {
      setCurrentAnimation({
        ...stableJpointsChanges[0],
        allChanges: stableJpointsChanges,
      })
      setStableJpointsChanges([])
      setJpointsChanges([])
      previousLengthRef.current = 0
    }
  }, [stableJpointsChanges, currentAnimation, isWaitingForChanges, setJpointsChanges])

  // Reset on account switch
  useEffect(() => {
    setCurrentAnimation(null)
    setLottieJson(null)
    setIsWaitingForChanges(false)
    setStableJpointsChanges([])
    setJpointsChanges([])
    previousLengthRef.current = 0

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      audioRef.current = null
    }
    if (soundTimeoutRef.current) clearTimeout(soundTimeoutRef.current)
    if (animRef.current) {
      animRef.current.destroy()
      animRef.current = null
    }
  }, [member?.selectedAccountId])

  // Load JSON by theme & +/- points
  useEffect(() => {
    if (!currentAnimation && colorScheme !== lastColorScheme) return
    const loadAnimation = async () => {
      const isNegative = currentAnimation?.difference < 0
      const animationFile = isNegative
        ? colorScheme === 'dark'
          ? import('./lottie/animation-burned-points-dark.json')
          : import('./lottie/animation-burned-points-light.json')
        : colorScheme === 'dark'
          ? import('./lottie/animation-earned-points-dark.json')
          : import('./lottie/animation-earned-points-light.json')
      const module = await animationFile
      setLottieJson(module.default)
    }
    loadAnimation()
  }, [currentAnimation])

  useEffect(() => setLastColorScheme(colorScheme), [colorScheme])

  // Build text & zero tracking
  const updatedLottieJson = useMemo(() => {
    if (!lottieJson || !currentAnimation) return null
    const updatedJson = JSON.parse(JSON.stringify(lottieJson))
    const changes = currentAnimation.allChanges || [currentAnimation]

    updatedJson.assets?.forEach(asset => {
      asset.layers?.forEach(layer => {
        if (layer.ty === 5) {
          const tk = layer?.t?.d?.k?.[0]
          if (tk?.s) tk.s.tr = 0
        }
      })
    })

    updatedJson.assets?.forEach(asset => {
      const textLayers = (asset.layers || []).filter(l => l.ty === 5)
      if (!textLayers.length) return

      const lines = changes.map(point => {
        const label = IntlUtil.label(getPointsLabel(point.id))
        return formatPointsText(point.difference, label)
      })
      const fullText = lines.join('\r')

      const first = textLayers[0]
      const tk0 = first?.t?.d?.k?.[0]
      if (tk0?.s) {
        tk0.s.t = fullText
        tk0.s.j = 2
      }
      textLayers.slice(1).forEach(layer => {
        const tk = layer?.t?.d?.k?.[0]
        if (tk?.s) tk.s.t = ''
      })
    })

    return updatedJson
  }, [currentAnimation, lottieJson])

  // Sound
  useEffect(() => {
    if (!currentAnimation || !lottieJson || !enableSound) return
    const isNegative = currentAnimation.difference < 0
    const audio = new Audio(isNegative ? burnedSound : earnedSound)
    audioRef.current = audio
    if (isNegative) audio.play().catch(() => {})
    else {
      soundTimeoutRef.current = setTimeout(() => {
        audio.play().catch(() => {})
      }, 1000)
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
        audioRef.current = null
      }
      if (soundTimeoutRef.current) clearTimeout(soundTimeoutRef.current)
    }
  }, [currentAnimation, lottieJson, enableSound, member?.selectedAccountId])

  // Mount/update lottie + Arabic shaping patch + COMPLETE → clear
  useEffect(() => {
    if (!currentAnimation || !updatedLottieJson) return
    if (!containerRef.current) return

    // Destroy previous instance
    if (animRef.current) {
      animRef.current.removeEventListener?.('enterFrame', patchArabicGroups)
      animRef.current.removeEventListener?.('DOMLoaded', onDomLoaded)
      animRef.current.removeEventListener?.('complete', onComplete)
      animRef.current.destroy()
      animRef.current = null
    }

    // Always create a fresh copy and force native <text>
    const animationData = JSON.parse(JSON.stringify(updatedLottieJson))
    if (animationData.chars) delete animationData.chars

    // Define here so we can remove later
    const patchArabicGroups = () => {
      const svg = containerRef.current?.querySelector('svg.lottie-ar-container')
      if (!svg) return
      svg.querySelectorAll('g[aria-label]').forEach(g => {
        const full = g.getAttribute('aria-label') || ''
        const letters = Array.from(g.querySelectorAll(':scope > text'))
        if (letters.length === 1 && letters[0].textContent === full) return
        if (!letters.length) return

        const xs = []
        letters.forEach(letter => {
          const t = letter.getAttribute('transform')
          const m = t?.match(/translate\(([-\d.]+),\s*([-\d.]+)\)/)
          if (m) xs.push(parseFloat(m[1]))
        })
        const first = letters[0]
        const m0 = first.getAttribute('transform')?.match(/translate\(([-\d.]+),\s*([-\d.]+)\)/)
        const originalX = m0 ? parseFloat(m0[1]) : 0
        const originalY = -45
        const centerX = xs.length ? (Math.min(...xs) + Math.max(...xs)) / 2 : originalX

        while (g.firstChild) g.removeChild(g.firstChild)

        const t = document.createElementNS('http://www.w3.org/2000/svg', 'text')
        t.setAttribute('xml:space', 'preserve')
        t.setAttribute('direction', 'rtl')
        t.setAttribute('text-anchor', 'middle')
        t.setAttribute('dominant-baseline', 'alphabetic')
        t.setAttribute('x', String(centerX))
        t.setAttribute('y', String(originalY))
        t.classList.add('lottie-ar')
        t.style.unicodeBidi = 'plaintext'
        t.style.letterSpacing = '0'
        t.style.whiteSpace = 'pre'
        t.style.fontFamily = `"Noto Naskh Arabic","Amiri","Segoe UI","Arial",sans-serif`
        t.style.fontFeatureSettings = `"liga" 1, "calt" 1`
        t.textContent = full
        g.appendChild(t)
      })
    }

    const onDomLoaded = () => patchArabicGroups()

    const onComplete = () => {
      // IMPORTANT: clear currentAnimation so next burst can be picked
      setCurrentAnimation(null)
      // Optional: also reset previousLength so next debounce considers fresh count
      previousLengthRef.current = 0
      // Clean instance
      if (animRef.current) {
        animRef.current.removeEventListener('enterFrame', patchArabicGroups)
        animRef.current.removeEventListener('DOMLoaded', onDomLoaded)
        animRef.current.removeEventListener('complete', onComplete)
        animRef.current.destroy()
        animRef.current = null
      }
    }

    // Create new instance
    animRef.current = lottie.loadAnimation({
      container: containerRef.current,
      renderer: 'svg',
      loop: false,
      autoplay: true,
      animationData,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid meet',
        progressiveLoad: true,
        className: 'lottie-ar-container',
      },
    })

    // Wire events
    animRef.current.addEventListener('DOMLoaded', onDomLoaded)
    animRef.current.addEventListener('enterFrame', patchArabicGroups)
    animRef.current.addEventListener('complete', onComplete)

    // Cleanup if deps change early
    return () => {
      if (animRef.current) {
        animRef.current.removeEventListener('enterFrame', patchArabicGroups)
        animRef.current.removeEventListener('DOMLoaded', onDomLoaded)
        animRef.current.removeEventListener('complete', onComplete)
        animRef.current.destroy()
        animRef.current = null
      }
    }
  }, [currentAnimation, updatedLottieJson])

  if (!currentAnimation || !lottieJson) return null

  return ReactDOM.createPortal(
    <div
      id="lottieanimation"
      className="pointer-events-none fixed inset-0 z-[2147483647] flex h-screen w-full items-center justify-center"
    >
      <div ref={containerRef} className="h-full w-full" aria-hidden />
    </div>,
    document.body,
  )
}

export default PointsAnimation
